import { AddressState, VendorOperatingDays } from '@graphql';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { SparkThemeName } from '@spark-web/theme';
import Exclamation from '../assets/exclamation.svg?react';
import Tick from '../assets/tick.svg?react';

export const FINANCIAL_PRODUCT = {
  ACT_SHS: 'act-shs-loan',
  TAS_ESLS: 'tas-esls-loan',
  ACT_SLIP: 'act-slip-loan',
  BGL: 'brighte-green-loan',
  BPL: 'brighte-personal-loan',
  ACT_SFA: 'act-solar-for-apartments-loan',
  FLEX: 'brighte-flex',
  ELECTRIFY_2515: 'electrify-2515',
  CBA_PARTNER: 'cba-partner',
  CBA_RESIDENTIAL: 'commbank-residential',
  BRIGHTE_PAY: 'brighte-pay',
  HEUF_LOAN: 'brighte-heuf-green-loan',
};

export enum CustomerType {
  Business = 'Business',
  Residential = 'Residential',
}

export enum LoanType {
  Loan = 'Loan',
  PaymentPlan = 'PaymentPlan',
}

export enum VerificationType {
  SmsEmail = 'SmsEmail',
  Sms = 'Sms',
  Email = 'Email',
}

export const BRIGHTE_CONTACT_NUMBER = '1300 274 448';

// TODO(INT-923): Replace this filter list with a backend query that returns
// package-enabled enterprise partners (e.g. `enterprisePartners(packageEnabled: true)`)
// once the API is available. Used by QuoteRequestsPage to filter quote requests.
export const PACKAGE_ENTERPRISE_PARTNER_IDS = ['commbank', 'rewiring-au', 'racq'];

export const PACKAGE_FINANCIAL_PRODUCT_CONFIRMATION_REQUIRED = [FINANCIAL_PRODUCT.ELECTRIFY_2515];

export const PACKAGE_CATEGORY_SELECTION_MODE: Record<string, 'all' | 'partial'> = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: 'partial',
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: 'all',
};

export const PACKAGE_VENDOR_SELECTION_MODE: Record<string, 'single' | 'multi'> = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: 'single',
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: 'multi',
};

// CT-604: backend mappings - wait until backend is ready
export const PACKAGE_FINANCE_PROMOTION_OPTIONS: Record<string, { name: string; disclaimer: string }> = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: {
    name: 'CommBank 5% Introductory Offer',
    disclaimer: '',
  },
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: {
    name: 'Estimated Pilot Subsidy',
    disclaimer: '(Subject to income verification)',
  },
};

// labels
export const PACKAGE_HEADER_TITLE = 'Your package';

export const PACKAGE_ELIGIBILITY_MESSAGE: Record<string, { title: string; description: string }> = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: {
    title: 'You’re eligible for a 5% introductory cashback offer',
    description:
      'Great news, you’re eligible for a 5% cash back offer. The discount will be applied to your quote based on the products you need to electrify your home.',
  },
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: {
    title: 'You are eligible for a Pilot Subsidy',
    description:
      'The pilot subsidy will be applied to your quote based on the products you need to electrify your home. The subsidy amount displayed post product selection is indicative, and will be subject to income verification and subsidy limits.',
  },
};

export const PACKAGE_SELECTION_MESSAGE_TITLE = 'Select your products';

export const PACKAGE_SELECTION_MESSAGE: Record<string, string> = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: 'The following quotes have been supplied for your home upgrade.',
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]:
    'Based on the home energy assessment, the following quotes have been supplied for your home upgrade.',
};

export const PACKAGE_FINANCE_DISCLAIMER: Record<string, string> = {
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]:
    'The total package amount exceeds the maximum subsidy amount of the Electrify 2515 Community Pilot. A Brighte Green Loan or cash payment is required to cover the remaining amount.',
};

export const PACKAGE_SELECTION_SUBMIT_BUTTON = 'Submit product selection';

export const PACKAGE_SUBMIT_CHECKBOX: Record<string, JSX.Element> = {
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: (
    <Text size="small" data-testid="package-submit-checkbox">
      I consent to the{' '}
      <TextLink href="https://brighte.com.au/electrify-2515-terms" target="_blank" rel="noreferrer">
        Electrify 2515 Community Pilot Terms and Conditions
      </TextLink>
    </Text>
  ),
};

// constants
export const AUTH0_SCOPE =
  'offline_access openid profile phone email create:finance-applications-from-quote read:finance-applications update:finance-applications update:finance-applicants read:finance-quotes write:payment-methods read:payment-methods update:finance-quotes-opportunity-selection read:quote-requests read:finance-referrals';

export const BRIGHTE_ASSETS_URL = 'https://static-assets.prod.cloud.brighte.com.au';

export const CUSTOMER_APPLICATION_BASE_URL: Record<string, string> = {
  staging: 'https://applynow.staging.cloud.brighte.com.au',
  uat: 'https://applynow.uat.cloud.brighte.com.au',
  prod: 'https://applynow.brighte.com.au',
};

export const KNOWN_ERROR_MESSAGES = {
  DEFAULT: 'We’re sorry something went wrong here.',
  INVALID_QUOTE: "The quote you're trying to access is invalid.",
  EXPIRED_QUOTE: "The quote you're trying to access has expired.",
  ALREADY_SUBMITTED_QUOTE: "The quote you're trying to access has already been submitted.",
  REPAYMENT_SUMMARY_NOT_FOUND: 'We are still loading your available finance terms. Please try again in a few minutes',
  INTERNAL_SERVER_ERROR: 'Internal server error',
  USER_NOT_FOUND: 'Your user record could not be found.',
  USER_SUSPENDED: 'Your user record has been suspended.',
  FORBIDDEN: "We're sorry, you don't have permission to proceed.",
  VALIDATION_ERROR: 'Invalid user input. Please check the details and try again.',
  QUOTE_EXPIRED: 'This referral is no longer valid - please contact your vendor for a new referral.',
  INVALID_PACKAGE_SELECTION: 'There’s an issue with your selection. Please contact support for help.',
};

export const QUOTE_STATUSES = {
  COMPLETED: {
    icon: <Tick />,
    title: 'This application has been submitted already.',
    message: (
      <Text>
        You can download the{' '}
        <TextLink target="_blank" href="https://brighte.com.au/app">
          Brighte app
        </TextLink>{' '}
        to track the status or manage your application.
      </Text>
    ),
  },
  CANCELLED: {
    icon: <Exclamation />,
    title: "We're sorry, this quote has been cancelled.",
    message: (
      <Text>
        You can download the{' '}
        <TextLink target="_blank" href="https://brighte.com.au/app">
          Brighte app
        </TextLink>{' '}
        to track the status or manage your application.
      </Text>
    ),
  },
  EXPIRED: {
    icon: <Exclamation />,
    title: "We're sorry, this quote has expired.",
    message: (
      <Text>
        You can download the{' '}
        <TextLink target="_blank" href="https://brighte.com.au/app">
          Brighte app
        </TextLink>{' '}
        to track the status or manage your application.
      </Text>
    ),
  },
  ACCEPTED: {
    icon: <Tick />,
    title: 'Your package has been submitted',
    message: '',
  },
  DECLINED: {
    icon: <Exclamation />,
    title: 'This quote is no longer available.',
    message: '',
  },
};

export const E2515_REBATE_URL = 'https://www.electrify2515.org/pilot';

export const CREDIT_LICENSE_NUMBER = 'Australian Credit License Number 508217.';
export const ABN_NUMBER = '74 609 165 906';
export const PRIVACY_POLICY_URL = 'https://brighte.com.au/privacy';
export const WEBSITE_TERMS_URL = 'https://brighte.com.au/support/website-terms';

export const PAGE_FOOTER = {
  APPLY_BY_PHONE: 'Apply by phone',
  CALL_BRIGHTE: (
    <Text size="large" weight="semibold" tone="muted">
      Call Brighte on <TextLink href="tel:1300274448">1300 274 448</TextLink>
    </Text>
  ),
  MENTION_CODE: 'Mention this code:',
  APPLICATION_CODE: 'BGLTEST',
  WHO_IS_BRIGHTE: 'Who is Brighte?',
  COMPANY_DESCRIPTION:
    'Brighte is Australia’s leading on the spot finance platform for solar, batteries and your next home improvements. Brighte enables you to enjoy your purchases now and pay it back over fortnightly instalments.',
  ACKNOWLEDGEMENT_OF_COUNTRY: 'Acknowledgement of Country',
  BRIGHTE_ACKNOWLEDGEMENT_OF_COUNTRY:
    'Brighte acknowledges Aboriginal and Torres Strait Islander peoples as the traditional custodians of our land – Australia. We pay our respect to them and their cultures and to the elders past, present and emerging. The Gadigal of the Eora Nation are the traditional custodians of the land on which Brighte’s office stands.',
  APPLICATION_SUBJECT_TO_APPROVAL:
    'All applications are subject to Brighte’s credit approval criteria. Fees, Terms and Conditions apply.',
  BRIGHTE_BUSINESS_DETAILS: ` Australian Business Number ${ABN_NUMBER}. ${CREDIT_LICENSE_NUMBER}`,
  BRIGHTE_CAPITAL: `© ${new Date().getFullYear()} Brighte Capital Pty Ltd`,
  TERMS_CONDITIONS: 'Terms and conditions',
  PRIVACY_POLICY: 'Privacy policy',
  TERMS: {
    [FINANCIAL_PRODUCT.ELECTRIFY_2515]: 'https://brighte.com.au/electrify-2515-terms',
  },
  TERMS_LOANS: 'https://brighte.com.au/support/brighte-personal-loan-agreement',
  SITE_TERMS: 'Site terms',
};

export const PAYMENT_PAGE_OPTIONS = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: {
    title: 'CommBank financing options',
    description: 'Get a cashback offer with a discounted interest rate.',
  },
  default: {
    title: 'Brighte finance',
    description: 'Get an interest-free payment plan with Brighte finance',
  },
};

export const PACKAGE_VENDOR_HEADER_TITLE = (
  name: string,
  totalQuotedPackagesCount: number,
  totalPackagesCount: number,
) => {
  if (totalPackagesCount === totalQuotedPackagesCount) {
    return `It's time to select your package, ${name}`;
  } else if (totalQuotedPackagesCount >= 1) {
    return `Hi ${name}, your quotes are ready to review`;
  } else {
    return `Thanks ${name}, vendors will be in contact soon`;
  }
};

// TODO(INT-923): replace with API-driven partner config once fields are stable
export const PACKAGE_VENDOR_CONFIG = (
  partnerId?: string,
): {
  customName: string | null;
  showVerifyFooter: boolean;
  usePartnerForHygraph?: boolean;
} => {
  switch (partnerId) {
    case 'commbank':
      return {
        customName: 'Home Energy Upgrades',
        showVerifyFooter: true,
        usePartnerForHygraph: false,
      };
    case 'rewiring-au':
      return {
        customName: null,
        showVerifyFooter: false,
        usePartnerForHygraph: false,
      };
    case 'racq':
      return {
        customName: 'Home Upgrade Program',
        showVerifyFooter: false,
        usePartnerForHygraph: true,
      };
    default:
      return {
        customName: null,
        showVerifyFooter: true,
        usePartnerForHygraph: false,
      };
  }
};

export const PACKAGE_VENDOR_SUBHEADER_TITLE = (quotedPackageCount: number, totalPackageCount: number) => {
  if (quotedPackageCount === totalPackageCount) {
    return `All ${totalPackageCount} packages are ready to review`;
  } else if (quotedPackageCount === 1) {
    return 'Your first package is ready to review';
  } else {
    return `${quotedPackageCount} of ${totalPackageCount} packages are ready to review`;
  }
};

export const PACKAGE_QUOTES = {
  READY_QUOTES: 'Ready for review',
  AWAITING_QUOTES: 'Awaiting quotes',
};

export const PACKAGE_VENDOR_SUBHEADER_WAITING_QUOTE = 'Waiting on quotes';

export enum KnownErrors {
  NotFound = 'No Quote found',
  ValidationError = 'Validation Error',
  QuoteExpired = 'Quote belongs to a product configuration that has expired.',
  InvalidPackageSelection = 'Package cannot contain multiple quotes from the same category.',
}

export enum FeatureFlagKeys {
  Maintenance = 'maintenance-page-customer-connect',
  ProceedToApplicationForm = 'proceed-to-application-form-for-accepted-quote',
  UniversalLogin = 'universal-login',
}

export const BRIGHTE_SITE_DOMAIN: Record<string, string> = {
  staging: 'staging.brighte.site',
  uat: 'uat.brighte.site',
  prod: 'brighte.com.au',
};

export const PATH_BY_PARTNER: Record<string, Path> = {
  commbank: 'cba',
  'rewiring-au': 'electrify2515',
  default: 'default',
};
export const PARTNER_THEME: Record<string, SparkThemeName> = {
  commbank: 'pantheon',
};

export type Path = 'cba' | 'default' | 'electrify2515';

export const DAYS_OF_WEEK = [
  VendorOperatingDays.Mon,
  VendorOperatingDays.Tue,
  VendorOperatingDays.Wed,
  VendorOperatingDays.Thu,
  VendorOperatingDays.Fri,
  VendorOperatingDays.Sat,
  VendorOperatingDays.Sun,
];

export const AU_STATE_TO_TIMEZONE: Record<string, string> = {
  [AddressState.NSW]: 'Australia/Sydney',
  [AddressState.VIC]: 'Australia/Melbourne',
  [AddressState.QLD]: 'Australia/Brisbane',
  [AddressState.SA]: 'Australia/Adelaide',
  [AddressState.WA]: 'Australia/Perth',
  [AddressState.TAS]: 'Australia/Hobart',
  [AddressState.NT]: 'Australia/Darwin',
  [AddressState.ACT]: 'Australia/Sydney',
};

export const MARKETPLACE_TRACKING_PAGE_URL: Record<string, string> = {
  staging: 'https://marketplace.staging.cloud.brighte.com.au',
  uat: 'https://marketplace.uat.cloud.brighte.com.au',
  prod: 'https://marketplace.brighte.com.au',
};

export const QUOTES_REQUESTS_TITLE: Record<string, string> = {
  commbank: 'CommBank Home Energy Upgrades',
  racq: 'RACQ Home Upgrade Program',
};
